import { Mic, Award, TrendingUp, BarChart3, Zap, Users } from "lucide-react";

export function HowItWorks() {
  return (
    <>
      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-14 sm:py-20 px-5 sm:px-6 lg:px-8 bg-[#F8FAFC]" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-xs sm:text-sm font-semibold text-[#14B8A6] uppercase tracking-widest mb-3 sm:mb-4">How It Works</h2>
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3B] mb-10 sm:mb-14">Three Steps to Readiness</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-8 relative">
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-slate-200" />
            {[
              { step: "01", icon: Mic, color: "#14B8A6", title: "Practice", desc: "Select a scenario and speak with an AI customer in your preferred language. No typing — pure voice." },
              { step: "02", icon: Award, color: "#2563EB", title: "Coaching", desc: "Get a Readiness Score, brutal truth feedback, missed moments, and what a top performer would say." },
              { step: "03", icon: TrendingUp, color: "#F59E0B", title: "Perform", desc: "Track improvement over time. Managers see team readiness at a glance. Ship field-ready advisors." },
            ].map(({ step, icon: Icon, color, title, desc }) => (
              <div key={step} className="relative">
                <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-full flex items-center justify-center mb-4 sm:mb-6 relative z-10" style={{ backgroundColor: `${color}10` }}>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-7 h-7 sm:w-8 sm:h-8" style={{ color }} />
                  </div>
                </div>
                <div className="text-[10px] sm:text-xs font-bold text-slate-300 uppercase tracking-widest mb-1.5 sm:mb-2">Step {step}</div>
                <h4 className="text-lg sm:text-xl font-bold text-[#0B1F3B] mb-2 sm:mb-3">{title}</h4>
                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT PREVIEW */}
      <section className="py-14 sm:py-20 px-5 sm:px-6 lg:px-8" data-testid="preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-xs sm:text-sm font-semibold text-[#2563EB] uppercase tracking-widest mb-3 sm:mb-4">Product Preview</h2>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3B]">Built for Enterprise Sales Teams</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
            {[
              { title: "Voice Role Play", desc: "Large mic button, real-time transcript, persona voices, and waveform animations", color: "#14B8A6", icon: Mic },
              { title: "Coaching Report", desc: "Circular readiness score, brutal truth, missed opportunities, and top performer examples", color: "#2563EB", icon: Award },
              { title: "Manager Dashboard", desc: "Team readiness overview, competency radar, rep tracker, and coaching priorities", color: "#F59E0B", icon: BarChart3 },
            ].map(({ title, desc, color, icon: Icon }) => (
              <div key={title} className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-slate-200/60 hover:shadow-xl transition-all duration-300">
                <div className="h-36 sm:h-48 flex items-center justify-center" style={{ backgroundColor: `${color}08` }}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-8 h-8 sm:w-10 sm:h-10" style={{ color }} />
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <h4 className="text-sm sm:text-base font-bold text-[#0B1F3B] mb-1.5 sm:mb-2">{title}</h4>
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS IMPACT */}
      <section className="py-14 sm:py-20 px-5 sm:px-6 lg:px-8 bg-[#0B1F3B]" data-testid="impact-section">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-xs sm:text-sm font-semibold text-[#14B8A6] uppercase tracking-widest mb-3 sm:mb-4">Business Impact</h2>
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-10 sm:mb-14">Why Leadership Invests in This</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8">
            {[
              { metric: "40%", label: "Faster Ramp-Up", desc: "New advisors reach field-readiness in weeks, not months", icon: Zap },
              { metric: "2x", label: "Better Conversion", desc: "Practiced advisors handle objections and close with confidence", icon: TrendingUp },
              { metric: "100%", label: "Scalable Coaching", desc: "Every rep gets personalized feedback — no manager bottleneck", icon: Users },
            ].map(({ metric, label, desc, icon: Icon }) => (
              <div key={label} className="p-6 sm:p-8 bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/10">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#14B8A6]/20 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#14B8A6]" />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-white mb-1.5 sm:mb-2">{metric}</p>
                <p className="text-[#14B8A6] font-semibold text-sm sm:text-base mb-1.5 sm:mb-2">{label}</p>
                <p className="text-slate-400 text-xs sm:text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
